import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { config } from '@/core/config'
import {
  BarChart3,
  Building2,
  FileText,
  Receipt,
  Users,
  Users2,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  Calendar,
  CreditCard,
  ShoppingBag,
  Package,
  Truck,
  ShoppingCart,
  CheckSquare,
  Workflow,
  Settings,
  Database,
  Percent,
  Building,
  Bell,
  BookOpen,
  Calculator,
  UserCheck,
  UserPlus,
  DollarSign,
  ClipboardList,
  ArrowLeftRight,
  FolderKanban,
  HardDrive,
  Trash2,
  Clock,
  Shield,
  Layers,
  Tags,
  PackagePlus,
  Ban,
  Gift,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useUIStore } from '@/store/ui.store'
import { createRTLClassName } from '@/lib/rtl'
import { Logo } from '@/components/shared/Logo'
import { useAuthStore } from '@/store/auth.store'
import { permissionsService } from '@/services/permissionsService'

interface NavSubItem {
  path?: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  roles?: string[]
  subItems?: NavSubItem[]
}

interface NavGroup {
  label: string
  icon: React.ComponentType<{ className?: string }>
  items: NavSubItem[]
  roles?: string[]
}

/**
 * Professional sidebar with grouped navigation structure and mobile responsiveness
 */
export const Sidebar: React.FC = () => {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { sidebarOpen, setSidebarOpen, setSidebarHovered } = useUIStore()
  
  // Track which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['dashboards'])
  // Track which nested items are expanded
  const [expandedSubItems, setExpandedSubItems] = useState<string[]>([])
  // Track if we've already auto-expanded for the current pathname
  const autoExpandedRef = useRef<string>('')

  // Auto-expand Reports submenu, Accounting submenu, Sales submenu, Fixed Assets submenu, and Finance group when on relevant pages
  useEffect(() => {
    const financeLabel = t('navigation.finance')
    const accountingLabel = t('navigation.accounting')
    const reportsLabel = t('navigation.reports')
    const salesLabel = t('navigation.sales')
    const fixedAssetsLabel = t('navigation.fixedAssets')
    const posLabel = 'POS'
    
    if (location.pathname.startsWith('/point-of-sales') && autoExpandedRef.current !== location.pathname) {
      setExpandedSubItems(prev => {
        if (!prev.includes(posLabel)) {
          return [...prev, posLabel]
        }
        return prev
      })
      
      setExpandedGroups(prev => {
        if (!prev.includes(financeLabel)) {
          return [...prev, financeLabel]
        }
        return prev
      })
      
      autoExpandedRef.current = location.pathname
    } else if ((location.pathname.startsWith('/fixed-assets') || location.pathname.startsWith('/asset-disposals')) && autoExpandedRef.current !== location.pathname) {
      setExpandedSubItems(prev => {
        if (!prev.includes(fixedAssetsLabel)) {
          return [...prev, fixedAssetsLabel]
        }
        return prev
      })
      
      setExpandedGroups(prev => {
        if (!prev.includes(financeLabel)) {
          return [...prev, financeLabel]
        }
        return prev
      })
      
      autoExpandedRef.current = location.pathname
    } else if (location.pathname.startsWith('/reports/') && autoExpandedRef.current !== location.pathname) {
      setExpandedSubItems(prev => {
        if (!prev.includes(reportsLabel)) {
          return [...prev, reportsLabel]
        }
        return prev
      })
      
      setExpandedGroups(prev => {
        if (!prev.includes(financeLabel)) {
          return [...prev, financeLabel]
        }
        return prev
      })
      
      autoExpandedRef.current = location.pathname
    } else if ((location.pathname.startsWith('/general-entries') || location.pathname.startsWith('/charts-of-accounts')) && autoExpandedRef.current !== location.pathname) {
      setExpandedSubItems(prev => {
        if (!prev.includes(accountingLabel)) {
          return [...prev, accountingLabel]
        }
        return prev
      })
      
      setExpandedGroups(prev => {
        if (!prev.includes(financeLabel)) {
          return [...prev, financeLabel]
        }
        return prev
      })
      
      autoExpandedRef.current = location.pathname
    } else if ((location.pathname.startsWith('/customers') || location.pathname.startsWith('/invoices') || location.pathname.startsWith('/quotations') || location.pathname.startsWith('/credit-notes') || location.pathname.startsWith('/customer-advances') || location.pathname.startsWith('/receipts') || location.pathname.startsWith('/receipt-returns') || location.pathname.startsWith('/projects')) && autoExpandedRef.current !== location.pathname) {
      setExpandedSubItems(prev => {
        if (!prev.includes(salesLabel)) {
          return [...prev, salesLabel]
        }
        return prev
      })
      
      setExpandedGroups(prev => {
        if (!prev.includes(financeLabel)) {
          return [...prev, financeLabel]
        }
        return prev
      })
      
      autoExpandedRef.current = location.pathname
    } else if (location.pathname.startsWith('/purchases/') && autoExpandedRef.current !== location.pathname) {
      const purchasesLabel = t('navigation.purchases')
      setExpandedSubItems(prev => {
        if (!prev.includes(purchasesLabel)) {
          return [...prev, purchasesLabel]
        }
        return prev
      })
      
      setExpandedGroups(prev => {
        if (!prev.includes(financeLabel)) {
          return [...prev, financeLabel]
        }
        return prev
      })
      
      autoExpandedRef.current = location.pathname
    } else if (location.pathname.startsWith('/inventory') && autoExpandedRef.current !== location.pathname) {
      const inventoryLabel = t('navigation.inventory')
      const innerInventoryLabel = t('navigation.inventoryInnerMenu')
      const itemsMenuLabel = t('navigation.inventoryItemsMenu')
      const purchasesMenuLabel = t('navigation.purchases')
      const salesMenuLabel = t('navigation.inventorySalesMenu')
      const reportsMenuLabel = t('navigation.inventoryReportsMenu')
      const schemesMenuLabel = t('navigation.inventorySchemesDiscountsMenu')
      setExpandedGroups((prev) => (prev.includes(inventoryLabel) ? prev : [...prev, inventoryLabel]))
      if (
        location.pathname.startsWith('/inventory/warehouses') ||
        location.pathname.startsWith('/inventory/adjustments') ||
        location.pathname.startsWith('/inventory/transfers') ||
        location.pathname.startsWith('/inventory/movements') ||
        location.pathname.startsWith('/inventory/unsellable-movement') ||
        location.pathname.startsWith('/inventory/dispatch') ||
        location.pathname.startsWith('/inventory/received')
      ) {
        setExpandedSubItems((prev) => {
          const rest = prev.filter(
            (l) =>
              l !== innerInventoryLabel &&
              l !== itemsMenuLabel &&
              l !== purchasesMenuLabel &&
              l !== salesMenuLabel &&
              l !== reportsMenuLabel &&
              l !== schemesMenuLabel,
          )
          return [...rest, innerInventoryLabel]
        })
      } else if (
        location.pathname.startsWith('/inventory/purchase-orders') ||
        location.pathname.startsWith('/inventory/goods-receipt-notes') ||
        location.pathname.startsWith('/inventory/goods-return-notes')
      ) {
        setExpandedSubItems((prev) => {
          const rest = prev.filter(
            (l) =>
              l !== innerInventoryLabel &&
              l !== itemsMenuLabel &&
              l !== purchasesMenuLabel &&
              l !== salesMenuLabel &&
              l !== reportsMenuLabel &&
              l !== schemesMenuLabel,
          )
          return [...rest, purchasesMenuLabel]
        })
      } else if (
        location.pathname.startsWith('/inventory/sales-orders') ||
        location.pathname.startsWith('/inventory/quotations') ||
        location.pathname.startsWith('/inventory/delivery-challan')
      ) {
        setExpandedSubItems((prev) => {
          const rest = prev.filter(
            (l) =>
              l !== innerInventoryLabel &&
              l !== itemsMenuLabel &&
              l !== purchasesMenuLabel &&
              l !== salesMenuLabel &&
              l !== reportsMenuLabel &&
              l !== schemesMenuLabel,
          )
          return [...rest, salesMenuLabel]
        })
      } else if (location.pathname.startsWith('/inventory/reports')) {
        setExpandedSubItems((prev) => {
          const rest = prev.filter(
            (l) =>
              l !== innerInventoryLabel &&
              l !== itemsMenuLabel &&
              l !== purchasesMenuLabel &&
              l !== salesMenuLabel &&
              l !== reportsMenuLabel &&
              l !== schemesMenuLabel,
          )
          return [...rest, reportsMenuLabel]
        })
      } else if (location.pathname.startsWith('/inventory/schemes-discounts')) {
        setExpandedSubItems((prev) => {
          const rest = prev.filter(
            (l) =>
              l !== innerInventoryLabel &&
              l !== itemsMenuLabel &&
              l !== purchasesMenuLabel &&
              l !== salesMenuLabel &&
              l !== reportsMenuLabel &&
              l !== schemesMenuLabel,
          )
          return [...rest, schemesMenuLabel]
        })
      } else {
        setExpandedSubItems((prev) => {
          const rest = prev.filter(
            (l) =>
              l !== innerInventoryLabel &&
              l !== itemsMenuLabel &&
              l !== purchasesMenuLabel &&
              l !== salesMenuLabel &&
              l !== reportsMenuLabel &&
              l !== schemesMenuLabel,
          )
          return [...rest, itemsMenuLabel]
        })
      }
      autoExpandedRef.current = location.pathname
    } else if ((location.pathname.startsWith('/workflow') || location.pathname.startsWith('/approval-jobs')) && autoExpandedRef.current !== location.pathname) {
      const workflowLabel = t('navigation.workflow')
      setExpandedGroups(prev => {
        if (!prev.includes(workflowLabel)) return [...prev, workflowLabel]
        return prev
      })
      autoExpandedRef.current = location.pathname
    } else if (location.pathname.startsWith('/supply-chain') && autoExpandedRef.current !== location.pathname) {
      const supplyChainLabel = t('navigation.supplyChain')
      setExpandedGroups((prev) => (prev.includes(supplyChainLabel) ? prev : [...prev, supplyChainLabel]))
      autoExpandedRef.current = location.pathname
    } else if (!location.pathname.startsWith('/reports/') && !location.pathname.startsWith('/general-entries') && !location.pathname.startsWith('/charts-of-accounts') && !location.pathname.startsWith('/customers') && !location.pathname.startsWith('/invoices') && !location.pathname.startsWith('/quotations') && !location.pathname.startsWith('/credit-notes') && !location.pathname.startsWith('/customer-advances') && !location.pathname.startsWith('/receipts') && !location.pathname.startsWith('/receipt-returns') && !location.pathname.startsWith('/projects') && !location.pathname.startsWith('/purchases/') && !location.pathname.startsWith('/fixed-assets') && !location.pathname.startsWith('/asset-disposals') && !location.pathname.startsWith('/payment-returns') && !location.pathname.startsWith('/workflow') && !location.pathname.startsWith('/approval-jobs') && !location.pathname.startsWith('/inventory')) {
      // Reset when leaving relevant pages
      autoExpandedRef.current = ''
    }
  }, [location.pathname, t])

  // ============================================
  // NAVIGATION VISIBILITY CONFIGURATION
  // ============================================
  // To hide/show navigation items, modify the values below:
  // 
  // HOW TO HIDE ITEMS:
  //   - Set the value to `true` to HIDE the item
  //   - Set the value to `false` to SHOW the item
  //
  // HOW TO SHOW HIDDEN ITEMS AGAIN:
  //   - Simply change `true` to `false` for the item you want to show
  //
  // EXAMPLES:
  //   '/hr': true,        // This hides "HR Dashboard"
  //   '/hr': false,       // This shows "HR Dashboard"
  //
  // NOTE: Items are NOT removed from code, only hidden from display.
  //       Routes still exist and can be accessed directly via URL.
  // ============================================
  const hiddenNavItems = {
    // Dashboard items
    '/hr': true,                    // HR Dashboard
    '/employee-dashboard': true,    // Employee Dashboard
    
    // HR items
    '/attendance': true,            // Attendance
    '/payroll': false,              // Generate Payroll (under Payroll) – show
    '/self-services': false,        // Self Services
    '/self-services/loan-requests': false, // Loan Request
    '/self-services/expense-claims': false, // Expense Claim — temporarily hidden (set to false to show again)
    '/self-services/leave-requests': false, // Leave Request
    '/self-services/hire-employee': false, // Hire Employee
    '/self-services/item-requests': false, // Item Request
    
    // Inventory (sidebar only — routes still work via URL)
    '/inventory/adjustments': false,  // Inventory adjustments — set false to show again
    '/inventory/transfers': false,   // Inventory transfers — set false to show again
    '/inventory/received': true,     // Inventory Received — hidden; set false to show again

    // Entire groups (hide by group label key)
    'salesInventory': false,        // Sales Inventory group
    'approvalWorkflows': false,     // Workflow group (Approval Master, Tasks Inbox)
    
    // Settings items
    '/taxes': false,                // Taxes
    // '/banks': true,              // Banks - Commented out to show HR Bank module
    '/notifications': true,         // Notifications
  }
  // ============================================

  /** Sidebar modules — API: GetPermissionForLoggedInUser, empty menuName = module-level; hide group + matching dashboard link if no moduleId */
  const [hrModuleAllowed, setHrModuleAllowed] = useState(false)
  const [financeModuleAllowed, setFinanceModuleAllowed] = useState(false)
  const [authModuleAllowed, setAuthModuleAllowed] = useState(false)
  const [mngModuleAllowed, setMngModuleAllowed] = useState(false)
  const [selfServiceModuleAllowed, setSelfServiceModuleAllowed] = useState(false)
  const [inventoryModuleAllowed, setInventoryModuleAllowed] = useState(false)
  const [workflowModuleAllowed, setWorkflowModuleAllowed] = useState(false)
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)
  /** Must match Swagger: GetPermissionForLoggedInUser ?roleId=… — role for the selected branch (branch selection screen “Role ID”). */
  const branchUserRoleId = useAuthStore((s) => s.user?.selectedBranch?.branchUserRoleId)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    let cancelled = false
    if (!isAuthenticated || branchId == null || branchUserRoleId == null) {
      setHrModuleAllowed(false)
      setFinanceModuleAllowed(false)
      setAuthModuleAllowed(false)
      setMngModuleAllowed(false)
      setSelfServiceModuleAllowed(false)
      setInventoryModuleAllowed(false)
      setWorkflowModuleAllowed(false)
      return
    }
    const roleId = branchUserRoleId
    void (async () => {
      const [hrRes, financeRes, authRes, mngRes, selfServiceRes, inventoryRes, workflowRes] =
        await Promise.allSettled([
          permissionsService.getPermissionForLoggedInUser('HR', '', branchId, roleId),
          permissionsService.getPermissionForLoggedInUser('Finance', '', branchId, roleId),
          permissionsService.getPermissionForLoggedInUser('AUTH', '', branchId, roleId),
          permissionsService.getPermissionForLoggedInUser('MNG', '', branchId, roleId),
          permissionsService.getPermissionForLoggedInUser('Self Service', '', branchId, roleId),
          permissionsService.getPermissionForLoggedInUser('Inventory', '', branchId, roleId),
          permissionsService.getPermissionForLoggedInUser('Work Flow', '', branchId, roleId),
        ])
      if (cancelled) return
      const ok = (r: PromiseSettledResult<{ moduleId: number | null }>) =>
        r.status === 'fulfilled' && r.value.moduleId != null && r.value.moduleId > 0
      setHrModuleAllowed(ok(hrRes))
      setFinanceModuleAllowed(ok(financeRes))
      setAuthModuleAllowed(ok(authRes))
      setMngModuleAllowed(ok(mngRes))
      setSelfServiceModuleAllowed(ok(selfServiceRes))
      setInventoryModuleAllowed(ok(inventoryRes))
      setWorkflowModuleAllowed(ok(workflowRes))
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, branchId, branchUserRoleId])

  // Navigation groups configuration
  const navGroups: NavGroup[] = [
    {
      label: t('navigation.dashboards'),
      icon: BarChart3,
      items: [
        { path: '/finance', label: t('navigation.financeDashboard'), icon: TrendingUp },
        { path: '/hr', label: t('navigation.hrDashboard'), icon: Users2 },
        { path: '/employee-dashboard', label: t('navigation.employeeDashboard'), icon: Users },
      ]
    },
    {
      label: t('navigation.hr'),
      icon: Users2,
      items: [
        {
          label: t('navigation.employee'),
          icon: Users,
          subItems: [
            { path: '/employees', label: t('navigation.employees'), icon: Users },
            { path: '/emp-documents', label: t('navigation.empDocuments'), icon: FileText },
            { path: '/emp-addresses', label: t('navigation.empAddress'), icon: FileText },
            { path: '/qualifications', label: t('navigation.qualifications'), icon: FileText },
            { path: '/skills', label: t('navigation.skills'), icon: FileText },
            { path: '/trainings', label: t('navigation.trainings'), icon: FileText },
            { path: '/vehicles', label: t('navigation.vehicle'), icon: FileText },
            { path: '/medicals', label: t('navigation.medicals'), icon: FileText },
            { path: '/media-links', label: t('navigation.socialMediaLinks'), icon: FileText },
            { path: '/contracts', label: t('contracts.title'), icon: FileText },
            { path: '/banks', label: t('navigation.bank'), icon: CreditCard },
          ],
        },
        {
          label: t('navigation.payroll'),
          icon: Calculator,
          subItems: [
            { path: '/payroll', label: t('navigation.generatePayroll'), icon: Calculator },
            { path: '/payroll/payroll-payment', label: t('navigation.payrollPayment'), icon: CreditCard },
            { path: '/payroll/report', label: t('navigation.payrollReport'), icon: FileText },
            { path: '/payroll/compare', label: t('navigation.payrollCompareReport'), icon: BarChart3 },
            { path: '/payroll/attendance', label: t('navigation.attendance'), icon: Calendar },
            { path: '/payroll/attendance/report', label: t('navigation.attendanceReport'), icon: FileText },
            { path: '/payroll/award-bonus', label: t('navigation.awardBonus'), icon: Calculator },
            { path: '/payroll/incentives', label: t('navigation.incentives'), icon: Calculator },
          ],
        },
       
      ]
    },
    {
      label: t('navigation.selfServices'),
      icon: UserCheck,
      items: [
        { path: '/self-services/loan-requests', label: t('navigation.loanRequest'), icon: FileText },
        { path: '/self-services/expense-claims', label: t('navigation.expenseClaim'), icon: Receipt },
        { path: '/self-services/leave-requests', label: t('navigation.leaveRequest'), icon: Calendar },
        { path: '/self-services/hire-employee', label: t('navigation.hireEmployee'), icon: UserPlus },
        { path: '/self-services/item-requests', label: t('navigation.itemRequest'), icon: ClipboardList },
        { path: '/self-services/overtime-requests', label: t('navigation.overtimeRequest'), icon: Clock },
      ],
    },
    {
      label: t('navigation.inventory'),
      icon: Package,
      items: [
        { path: '/inventory/warehouses', label: t('navigation.warehouses'), icon: Building },
        {
          path: '/inventory/adjustments',
          label: t('navigation.inventoryAdjustments'),
          icon: ClipboardList,
        },
        {
          path: '/inventory/movements',
          label: t('navigation.inventoryTransfers'),
          icon: ArrowLeftRight,
        },
        {
          path: '/inventory/unsellable-movement',
          label: t('navigation.inventoryUnsellableMovement'),
          icon: Ban,
        },
        {
          path: '/inventory/dispatch',
          label: t('navigation.inventoryDispatch'),
          icon: Truck,
        },
        {
          path: '/inventory/received',
          label: t('navigation.inventoryReceived'),
          icon: PackagePlus,
        },
        {
          label: t('navigation.inventoryItemsMenu'),
          icon: Package,
          subItems: [
            { path: '/inventory/items', label: t('navigation.items'), icon: Package },
            { path: '/inventory/item-batches', label: t('navigation.itemBatches'), icon: Layers },
            { path: '/inventory/categories', label: t('navigation.categories'), icon: Tags },
          ],
        },
        {
          label: t('navigation.inventorySchemesDiscountsMenu'),
          icon: Percent,
          subItems: [
            {
              path: '/inventory/schemes-discounts/discounts',
              label: t('navigation.inventoryDiscounts'),
              icon: Percent,
            },
            {
              path: '/inventory/schemes-discounts/bonus-items',
              label: t('navigation.inventoryBonusItems'),
              icon: Gift,
            },
          ],
        },
        {
          label: t('navigation.purchases'),
          icon: ShoppingCart,
          subItems: [
            { path: '/inventory/purchase-orders', label: t('navigation.purchaseOrders'), icon: FileText },
            { path: '/inventory/goods-receipt-notes', label: t('navigation.goodsReceiptNotes'), icon: FileText },
            { path: '/inventory/goods-return-notes', label: t('navigation.goodsReturnNotes'), icon: FileText },
          ],
        },
        {
          label: t('navigation.inventorySalesMenu'),
          icon: ShoppingBag,
          subItems: [
            { path: '/inventory/sales-orders', label: t('navigation.inventorySalesOrders'), icon: FileText },
            { path: '/inventory/delivery-challan', label: t('navigation.inventoryDeliveryChallan'), icon: Truck },
            { path: '/inventory/quotations', label: t('navigation.inventorySalesQuotations'), icon: FileText },
          ],
        },
        {
          label: t('navigation.inventoryReportsMenu'),
          icon: BarChart3,
          subItems: [
            { path: '/inventory/reports/cogs', label: t('navigation.cogsReport'), icon: FileText },
            { path: '/inventory/reports/stock', label: t('navigation.stockReport'), icon: FileText },
            { path: '/inventory/reports/sales', label: t('navigation.salesReport'), icon: FileText },
          ],
        },
      ],
    },
    {
      label: t('navigation.supplyChain'),
      icon: Truck,
      items: [
        {
          path: '/supply-chain/purchase-requisitions',
          label: t('navigation.purchaseRequisitions'),
          icon: ClipboardList,
        },
        {
          path: '/supply-chain/purchase-orders',
          label: t('navigation.purchaseOrders'),
          icon: FileText,
        },
        {
          path: '/supply-chain/goods-receipt-notes',
          label: t('navigation.goodsReceiptNotes'),
          icon: FileText,
        },
      ],
    },
    {
      label: t('navigation.finance'),
      icon: DollarSign,
      items: [
        {
          label: 'POS',
          icon: ShoppingCart,
          subItems: [{ path: '/point-of-sales', label: 'Point Of Sales', icon: ShoppingCart }],
        },
        {
          label: t('navigation.sales'),
          icon: ShoppingBag,
          subItems: [
            { path: '/customers', label: t('navigation.customers'), icon: Building2 },
            { path: '/quotations', label: t('navigation.quotations'), icon: FileText },
            { path: '/invoices', label: t('navigation.invoices'), icon: Receipt },
            { path: '/receipts', label: t('navigation.receipts'), icon: Receipt },
            { path: '/credit-notes', label: t('navigation.creditNotes'), icon: ArrowLeftRight },
            { path: '/receipt-returns', label: t('navigation.receiptReturns'), icon: ArrowLeftRight },
            { path: '/projects', label: t('navigation.projects'), icon: FolderKanban },
            { path: '/customer-advances', label: t('navigation.customerAdvances'), icon: CreditCard },
          ]
        },
        {
          label: t('navigation.purchases'),
          icon: ShoppingCart,
          subItems: [
            { path: '/purchases/vendors', label: t('navigation.vendors'), icon: Truck },
            { path: '/purchases/bills', label: t('navigation.bills'), icon: Receipt },
            { path: '/purchases/payments', label: t('navigation.payments'), icon: CreditCard },
            { path: '/purchases/refunds', label: t('navigation.vendorCredits'), icon: ArrowLeftRight },
            { path: '/purchases/vendor-advances', label: t('navigation.vendorAdvances'), icon: DollarSign },
            { path: '/purchases/payment-returns', label: 'Payment Returns', icon: ArrowLeftRight },
          ]
        },
        {
          label: t('navigation.accounting'),
          icon: BookOpen,
          subItems: [
            { path: '/general-entries', label: t('navigation.generalEntries'), icon: FileText },
            { path: '/charts-of-accounts', label: t('navigation.chartsOfAccounts'), icon: BookOpen },
          ]
        },
        {
          label: t('navigation.reports'),
          icon: BarChart3,
          subItems: [
            { path: '/reports/account-ledger', label: t('navigation.accountLedgerReport'), icon: BookOpen },
            { path: '/reports/customer-ledger', label: t('navigation.customerLedgerReport'), icon: BarChart3 },
            { path: '/reports/vendor-ledger', label: t('navigation.vendorLedgerReport'), icon: BarChart3 },
            { path: '/reports/trial-balance', label: t('navigation.trialBalanceReport'), icon: FileText },
            { path: '/reports/aged-receivables', label: t('navigation.agedReceivablesReport'), icon: BarChart3 },
            { path: '/reports/aged-payables', label: t('navigation.agedPayablesReport'), icon: BarChart3 },
            { path: '/reports/vat-return', label: t('navigation.vatReturnReport'), icon: BarChart3 },
            { path: '/reports/sales-revenue', label: t('navigation.salesRevenueReport'), icon: BarChart3 },
          ]
        },
        {
          label: t('navigation.fixedAssets'),
          icon: HardDrive,
          subItems: [
            { path: '/fixed-assets', label: t('navigation.assets'), icon: Package },
            { path: '/asset-disposals', label: t('navigation.assetsDisposal'), icon: Trash2 },
            { path: '/fixed-assets/report', label: t('navigation.fixedAssetReport'), icon: BarChart3 },
          ]
        },
      ]
    },
    {
      label: t('navigation.workflow'),
      icon: Workflow,
      items: [
        { path: '/workflow/approval-master', label: t('navigation.approvalMaster'), icon: ClipboardList },
        { path: '/approval-jobs', label: t('navigation.tasksInbox'), icon: CheckSquare },
      ]
    },
    {
      label: t('navigation.security'),
      icon: Shield,
      items: [
        { path: '/security/roles', label: t('navigation.roles'), icon: Shield },
        { path: '/security/users', label: t('navigation.users'), icon: Users },
      ],
    },
    {
      label: t('navigation.settings'),
      icon: Settings,
      items: [
        { path: '/lookups', label: t('navigation.lookups'), icon: Database },
        { path: '/taxes', label: t('navigation.taxes'), icon: Percent },
        { path: '/accounts', label: t('navigation.accounts'), icon: BookOpen },
        { path: '/branches', label: t('navigation.branches'), icon: Building },
        { path: '/company-banks', label: t('navigation.companyBanks'), icon: Building2 },
        { path: '/shifts', label: 'Shift', icon: Clock },
        { path: '/gosi-setting', label: t('navigation.gosiSetting'), icon: Settings },
        { path: '/late-policy', label: t('navigation.latePolicy'), icon: Clock },
        { path: '/leave-policy', label: t('navigation.leavePolicy'), icon: Calendar },
        { path: '/notifications', label: t('navigation.notifications'), icon: Bell },
      ]
    }
  ]

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev => 
      prev.includes(groupLabel) 
        ? prev.filter(g => g !== groupLabel)
        : [...prev, groupLabel]
    )
  }

  const toggleSubItem = (itemLabel: string) => {
    setExpandedSubItems(prev => 
      prev.includes(itemLabel) 
        ? prev.filter(i => i !== itemLabel)
        : [...prev, itemLabel]
    )
  }

  const siblingPaths = (items?: NavSubItem[]) =>
    (items ?? []).map((item) => item.path).filter((path): path is string => Boolean(path))

  const isNavPathActive = (itemPath?: string, otherPaths: string[] = []) => {
    if (!itemPath) return false
    const pathname = location.pathname
    const matches = (path: string) => pathname === path || pathname.startsWith(`${path}/`)
    if (!matches(itemPath)) return false
    return !otherPaths.some(
      (path) => path !== itemPath && path.length > itemPath.length && matches(path),
    )
  }

  // Check if any item in a group is currently active
  const isGroupActive = (group: NavGroup) => {
    return group.items.some(item => {
      if (item.path && isNavPathActive(item.path, siblingPaths(group.items))) return true
      if (item.subItems) {
        const childPaths = siblingPaths(item.subItems)
        return item.subItems.some(subItem => isNavPathActive(subItem.path, childPaths))
      }
      return false
    })
  }

  // Check if a sub-item or any of its children is active
  const isSubItemActive = (item: NavSubItem) => {
    if (item.subItems) {
      const childPaths = siblingPaths(item.subItems)
      return item.subItems.some(subItem => isNavPathActive(subItem.path, childPaths))
    }
    return Boolean(isNavPathActive(item.path))
  }

  // Filter navigation items. Dashboards links (/finance, /hr, /employee-dashboard) are not gated by GetPermissionForLoggedInUser — only hiddenNavItems.
  const filterNavItems = (items: NavSubItem[]): NavSubItem[] => {
    return items
      .filter(item => {
        // Check if item path is hidden
        if (item.path && hiddenNavItems[item.path as keyof typeof hiddenNavItems]) {
          return false
        }
        // If item has subItems, filter them too
        if (item.subItems) {
          item.subItems = filterNavItems(item.subItems)
          // Hide parent if all children are hidden
          return item.subItems.length > 0
        }
        return true
      })
      .map(item => {
        // Recursively filter subItems
        if (item.subItems) {
          return {
            ...item,
            subItems: filterNavItems(item.subItems)
          }
        }
        return item
      })
  }

  // Filter navigation groups
  const filteredNavGroups = navGroups
    .filter(group => {
      if (!hrModuleAllowed && group.label === t('navigation.hr')) {
        return false
      }
      if (!financeModuleAllowed && group.label === t('navigation.finance')) {
        return false
      }
      if (!authModuleAllowed && group.label === t('navigation.security')) {
        return false
      }
      if (!mngModuleAllowed && group.label === t('navigation.settings')) {
        return false
      }
      if (!selfServiceModuleAllowed && group.label === t('navigation.selfServices')) {
        return false
      }
      if (!inventoryModuleAllowed && group.label === t('navigation.inventory')) {
        return false
      }
      const workflowLabel = t('navigation.workflow')
      if (!workflowModuleAllowed && group.label === workflowLabel) {
        return false
      }
      // Check if entire group should be hidden by comparing with translation keys
      const salesInventoryLabel = t('navigation.salesInventory')
      const approvalWorkflowsLabel = t('navigation.approvalWorkflows')
      
      if (hiddenNavItems['salesInventory' as keyof typeof hiddenNavItems] && group.label === salesInventoryLabel) {
        return false
      }
      if (hiddenNavItems['approvalWorkflows' as keyof typeof hiddenNavItems] && (group.label === approvalWorkflowsLabel || group.label === workflowLabel)) {
        return false
      }
      return true
    })
    .map(group => ({
      ...group,
      items: filterNavItems(group.items)
    }))
    .filter(group => group.items.length > 0) // Remove groups with no visible items

  // Close sidebar on mobile when clicking a link
  const handleLinkClick = () => {
    if (window.innerWidth < 1024) { // lg breakpoint
      setSidebarOpen(false)
    }
  }

  const sidebarClasses = createRTLClassName({
    common: `fixed top-0 h-full border-gray-200 dark:border-gray-800 z-50 transition-all duration-300 w-72 bg-white dark:bg-black`,
    ltr: `left-0 border-r ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`,
    rtl: `right-0 border-l ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`,
  })

  const overlayClasses = `fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300 ${
    sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
  }`

  return (
    <TooltipProvider>
      {/* Mobile Overlay */}
      <div 
        className={overlayClasses}
        onClick={() => setSidebarOpen(false)}
      />
      
      <aside
        className={sidebarClasses}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex h-full flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              <Logo 
                alt="ERP Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto">
          {filteredNavGroups.map((group) => {
            const GroupIcon = group.icon
            const isExpanded = expandedGroups.includes(group.label)
            const isActive = isGroupActive(group)

            return (
              <Collapsible
                key={group.label}
                open={isExpanded}
                onOpenChange={() => toggleGroup(group.label)}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className={createRTLClassName({
                      common: `w-full justify-between h-auto min-h-10 py-1.5 transition-all duration-200 px-3 ${
                        isActive 
                          ? 'bg-gradient-to-r from-primary/10 to-secondary/5 text-primary' 
                          : 'text-gray-600 dark:text-gray-400 hover:bg-primary/5 dark:hover:bg-primary/10 hover:text-primary dark:hover:text-primary'
                      }`,
                      ltr: '',
                      rtl: '',
                    })}
                  >
                    {/* LTR Layout: Icon + Text on left, Arrow on right */}
                    <div className={createRTLClassName({
                      common: "flex items-center justify-between w-full",
                      ltr: "block",
                      rtl: "hidden"
                    })}>
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                          <GroupIcon className="w-3 h-3 text-white" />
                        </div>
                        <span className="whitespace-normal break-words font-medium leading-tight">{group.label}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </div>
                    
                    {/* RTL Layout: Icon on left, Text, Arrow on right (swapped positions) */}
                    <div className={createRTLClassName({
                      common: "flex items-center justify-between w-full",
                      ltr: "hidden",
                      rtl: "block"
                    })}>
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center ml-3 flex-shrink-0">
                          <GroupIcon className="w-3 h-3 text-white" />
                        </div>
                        <span className="whitespace-normal break-words font-medium leading-tight">{group.label}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4 rotate-180" />
                      )}
                    </div>
                  </Button>
                </CollapsibleTrigger>
                
                <CollapsibleContent className="space-y-1">
                  {group.items.map((item) => {
                    const ItemIcon = item.icon
                    const hasSubItems = item.subItems && item.subItems.length > 0
                    const isSubItemExpanded = hasSubItems && expandedSubItems.includes(item.label)
                    const isItemActive = item.path ? location.pathname === item.path : false
                    const isSubItemGroupActive = hasSubItems && isSubItemActive(item)

                    if (hasSubItems) {
                      return (
                        <Collapsible
                          key={item.label}
                          open={isSubItemExpanded}
                          onOpenChange={() => toggleSubItem(item.label)}
                        >
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              className={createRTLClassName({
                                common: `w-full justify-between h-auto min-h-9 py-1.5 transition-all duration-200 px-3 ml-3 rtl:ml-0 rtl:mr-3 ${
                                  isSubItemGroupActive 
                                    ? 'bg-gray-100 dark:bg-gray-900/50 text-gray-900 dark:text-white border-l-2 border-gray-300 dark:border-gray-600 shadow-sm font-medium' 
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/30 hover:text-gray-900 dark:hover:text-white'
                                }`,
                                ltr: '',
                                rtl: '',
                              })}
                            >
                              <div className="flex items-center">
                                <ItemIcon className="w-3 h-3 flex-shrink-0 -mt-0.5 nav-icon" />
                                <span className="whitespace-normal break-words text-sm leading-tight ml-2">{item.label}</span>
                              </div>
                              {isSubItemExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-1 ml-6 rtl:ml-0 rtl:mr-6 border-l border-gray-200 dark:border-gray-700 rtl:border-l-0 rtl:border-r">
                            {item.subItems?.map((subItem) => {
                              const isSubItemActive = isNavPathActive(subItem.path, siblingPaths(item.subItems))
                              const SubItemIcon = subItem.icon

                              return (
                                <Button
                                  key={subItem.path}
                                  variant="ghost"
                                  className={createRTLClassName({
                                    common: `w-full justify-start h-auto min-h-9 py-1.5 transition-all duration-200 px-3 pl-4 rtl:pl-3 rtl:pr-4 ${
                                      isSubItemActive 
                                        ? 'bg-gray-100 dark:bg-gray-900/50 text-gray-900 dark:text-white border-l-2 border-gray-300 dark:border-gray-600 shadow-sm font-medium' 
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/30 hover:text-gray-900 dark:hover:text-white'
                                    }`,
                                    ltr: '',
                                    rtl: '',
                                  })}
                                  asChild
                                >
                                  <Link to={subItem.path!} onClick={handleLinkClick}>
                                    <SubItemIcon className="w-3 h-3 flex-shrink-0 -mt-0.5 nav-icon" />
                                    <span className="whitespace-normal break-words text-sm leading-tight">{subItem.label}</span>
                                  </Link>
                                </Button>
                              )
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      )
                    }

                    return (
                      <Button
                        key={item.path}
                        variant="ghost"
                        className={createRTLClassName({
                          common: `w-full justify-start h-auto min-h-9 py-1.5 transition-all duration-200 px-3 ml-3 rtl:ml-0 rtl:mr-3 ${
                            isItemActive 
                              ? 'bg-gray-100 dark:bg-gray-900/50 text-gray-900 dark:text-white border-l-2 border-gray-300 dark:border-gray-600 shadow-sm font-medium' 
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/30 hover:text-gray-900 dark:hover:text-white'
                          }`,
                          ltr: '',
                          rtl: '',
                        })}
                        asChild
                      >
                        <Link to={item.path!} onClick={handleLinkClick}>
                          <ItemIcon className="w-3 h-3 flex-shrink-0 -mt-0.5 nav-icon" />
                          <span className="whitespace-normal break-words text-sm leading-tight">{item.label}</span>
                        </Link>
                      </Button>
                    )
                  })}
                </CollapsibleContent>
              </Collapsible>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 pt-0">
          <div className="text-xs text-gray-400 text-center">
            {t('app.name')} v{config.app.version}
          </div>
        </div>
        </div>
      </aside>
    </TooltipProvider>
  )
}